import { useState } from 'react';
import { Edit2, Check, X } from 'lucide-react';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Textarea } from '../../../ui/textarea';
import { Label } from '../../../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../../ui/dialog';
import type { DiscoveredMethodItemDto } from '../../../../api/curated-integrations.types';
import { FormattedDescription } from './formatMethodDescription';

/** Override stored per method name. */
export interface MethodOverride {
  label?: string;
  description?: string;
}

interface MethodsTableProps {
  /** All methods discovered from the upstream MCP server. */
  methods: DiscoveredMethodItemDto[];
  /**
   * Set of currently enabled (whitelisted) method names.
   * Parent holds this state; changes bubble up via onEnabledMethodsChange.
   */
  enabledMethods: string[];
  /**
   * Per-method label/description overrides.
   * Parent holds this state; changes bubble up via onMethodOverridesChange.
   */
  methodOverrides: Record<string, MethodOverride>;
  /** Called when the user toggles a method's whitelist checkbox. */
  onEnabledMethodsChange: (enabledMethods: string[]) => void;
  /** Called when the user saves an override for a method. */
  onMethodOverridesChange: (overrides: Record<string, MethodOverride>) => void;
}

interface EditState {
  methodName: string;
  label: string;
  description: string;
}

/**
 * Table of discovered MCP methods with inline whitelist toggles and
 * a per-row override editor (label + description).
 *
 * State lifting: the parent (MethodsTab) owns enabledMethods + methodOverrides.
 */
export function MethodsTable({
  methods,
  enabledMethods,
  methodOverrides,
  onEnabledMethodsChange,
  onMethodOverridesChange,
}: MethodsTableProps) {
  const [editState, setEditState] = useState<EditState | null>(null);

  const toggleEnabled = (methodName: string) => {
    if (enabledMethods.includes(methodName)) {
      onEnabledMethodsChange(enabledMethods.filter((m) => m !== methodName));
    } else {
      onEnabledMethodsChange([...enabledMethods, methodName]);
    }
  };

  const openEdit = (method: DiscoveredMethodItemDto) => {
    const existing = methodOverrides[method.name];
    setEditState({
      methodName: method.name,
      label: existing?.label ?? method.label ?? method.name,
      description: existing?.description ?? method.description ?? '',
    });
  };

  const saveEdit = () => {
    if (!editState) return;
    const updated: Record<string, MethodOverride> = {
      ...methodOverrides,
      [editState.methodName]: {
        label: editState.label.trim() || undefined,
        description: editState.description.trim() || undefined,
      },
    };
    onMethodOverridesChange(updated);
    setEditState(null);
  };

  const cancelEdit = () => setEditState(null);

  if (methods.length === 0) {
    return (
      <p className="text-sm text-gray-500 italic">Keine Methoden gefunden.</p>
    );
  }

  return (
    <>
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full text-sm" role="table" aria-label="Entdeckte Methoden">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              <th scope="col" className="text-left py-2.5 px-3 font-semibold text-gray-600 w-8">
                <span className="sr-only">Aktiviert</span>
              </th>
              <th scope="col" className="text-left py-2.5 px-3 font-semibold text-gray-600 w-1/3">
                Methode
              </th>
              <th scope="col" className="text-left py-2.5 px-3 font-semibold text-gray-600">
                Beschreibung
              </th>
              <th scope="col" className="text-right py-2.5 px-3 font-semibold text-gray-600 w-16">
                <span className="sr-only">Aktionen</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {methods.map((method) => {
              const isEnabled = enabledMethods.includes(method.name);
              const override = methodOverrides[method.name];
              const displayLabel = override?.label ?? method.label ?? method.name;
              const hasOverride = !!override?.label || !!override?.description;

              return (
                <tr
                  key={method.name}
                  className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors"
                >
                  {/* Whitelist checkbox */}
                  <td className="py-2 px-3">
                    <input
                      type="checkbox"
                      id={`method-${method.name}`}
                      checked={isEnabled}
                      onChange={() => toggleEnabled(method.name)}
                      className="w-4 h-4 rounded accent-blue-600 cursor-pointer"
                      aria-label={`Methode ${method.name} ${isEnabled ? 'deaktivieren' : 'aktivieren'}`}
                    />
                  </td>

                  {/* Method name + override indicator */}
                  <td className="py-2 px-3">
                    <label
                      htmlFor={`method-${method.name}`}
                      className="font-mono text-xs text-blue-700 cursor-pointer select-none"
                    >
                      {displayLabel}
                    </label>
                    {hasOverride && (
                      <span
                        className="ml-1.5 text-xs bg-amber-100 text-amber-700 px-1 py-0.5 rounded"
                        title="Dieser Methode hat eine benutzerdefinierte Bezeichnung oder Beschreibung."
                        aria-label="Override vorhanden"
                      >
                        Override
                      </span>
                    )}
                    {method.name !== displayLabel && (
                      <div className="text-xs text-gray-400 font-mono mt-0.5">{method.name}</div>
                    )}
                  </td>

                  {/* Description */}
                  <td className="py-2 px-3 text-gray-600 text-xs align-top max-w-xl">
                    {override?.description ? (
                      <div className="whitespace-pre-line">{override.description}</div>
                    ) : (
                      <FormattedDescription text={method.description} />
                    )}
                  </td>

                  {/* Edit button */}
                  <td className="py-2 px-3 text-right">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => openEdit(method)}
                      aria-label={`Override für Methode ${method.name} bearbeiten`}
                      className="h-7 w-7 p-0 text-gray-400 hover:text-blue-600"
                    >
                      <Edit2 className="w-3.5 h-3.5" aria-hidden="true" />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Override editor dialog */}
      <Dialog open={editState !== null} onOpenChange={(open) => { if (!open) cancelEdit(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Methoden-Override bearbeiten</DialogTitle>
            <DialogDescription>
              Passen Sie Anzeigename und Beschreibung für{' '}
              <span className="font-mono font-medium text-gray-800">
                {editState?.methodName}
              </span>{' '}
              im Kundenportal an.
            </DialogDescription>
          </DialogHeader>

          {editState && (
            <div className="space-y-4 mt-2">
              <div className="space-y-1.5">
                <Label htmlFor="override-label">Anzeigename</Label>
                <Input
                  id="override-label"
                  value={editState.label}
                  onChange={(e) => setEditState((s) => s ? { ...s, label: e.target.value } : s)}
                  placeholder="Angezeigter Name im Kundenportal"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="override-desc">Beschreibung</Label>
                <Textarea
                  id="override-desc"
                  value={editState.description}
                  onChange={(e) => setEditState((s) => s ? { ...s, description: e.target.value } : s)}
                  placeholder="Beschreibung im Kundenportal"
                  rows={3}
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={cancelEdit}>
                  <X className="w-4 h-4 mr-1" aria-hidden="true" />
                  Abbrechen
                </Button>
                <Button type="button" onClick={saveEdit}>
                  <Check className="w-4 h-4 mr-1" aria-hidden="true" />
                  Speichern
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
